import { useEffect, useState } from 'react';

import {
	createLog,
	INVENTORY_FIELD_GROUP_CONFIGS,
	INVENTORY_SECTIONS,
	InventoryDataSource,
	LogSeverity,
	Services,
	type ServicesProvider,
} from '@ekhodealer/ekho-common';
import { Box, Button, Dropdown, Group,Stack, Text } from '@ekhodealer/ekho-common/rev';

import type { TableColumn } from '@common/components/Table/Table';
import type {
	FieldGroupPrecedence,	InventoryFieldGroup} from '@ekhodealer/ekho-common';

import InfoIcon from '@common/components/icons/InfoIcon';
import { Table } from '@common/components/Table/Table';
import { useCurrentServicesProvider } from '@common/hooks/useCurrentServicesProvider';

import { useCreateInventoryFieldGroupPrecedence } from '../../api/configuration/inventory/useCreateInventoryFieldGroupPrecedence';
import { useServiceProgramInfo } from '../../hooks/useServiceProgramInfo';

/**
 * Interface for table row data
 */
interface FieldGroupRow {
	id: string;
	fieldGroup: InventoryFieldGroup;
	name: string;
	description: string;
	selectedSource: InventoryDataSource;
	availableSources: InventoryDataSource[];
}

const EMPTY_FORM_DATA: Record<string, InventoryDataSource> = {};

/**
 * Helper function to get initial form data from ServicesProvider
 */
function getFormStateFromServicesProvider(
	servicesProvider: ServicesProvider | undefined | null,
): Record<string, InventoryDataSource> {
	if (!servicesProvider) {
		return EMPTY_FORM_DATA;
	}

	const existingPrecedence =
		servicesProvider.config?.[Services.INVENTORY]?.fieldGroupPrecedence || {};

	// Initialize with defaults from config if no precedence exists
	const formData: Record<string, InventoryDataSource> = {};
	Object.entries(INVENTORY_FIELD_GROUP_CONFIGS).forEach(([fieldGroup, config]) => {
		const group = fieldGroup as InventoryFieldGroup;
		formData[group] = existingPrecedence[group] || config.defaultSource;
	});

	return formData;
}

/**
 * Helper function to get field group precedence from form data
 */
function getConfigFromFormData(data: Record<string, InventoryDataSource>): FieldGroupPrecedence {
	if (!data) {
		throw new Error('Form data is required');
	}

	// Validate all required field groups are present
	const missingGroups = Object.keys(INVENTORY_FIELD_GROUP_CONFIGS).filter(
		(fieldGroup) => !data[fieldGroup as InventoryFieldGroup],
	);

	if (missingGroups.length > 0) {
		throw new Error(`Missing data source selections for: ${missingGroups.join(', ')}`);
	}

	return data as FieldGroupPrecedence;
}

/**
 * Helper to convert InventoryDataSource enum to readable labels
 */
function getDataSourceLabel(source: InventoryDataSource): string {
	switch (source) {
		case InventoryDataSource.DMS:
			return 'DMS';
		case InventoryDataSource.WEBSITE:
			return 'Website';
		case InventoryDataSource.OEM:
			return 'OEM';
		case InventoryDataSource.EKHO:
			return 'Ekho';
		default:
			return source;
	}
}

/**
 * Component for managing Inventory Service Field Group Precedence Configuration
 */
export function FieldGroupPrecedenceForm() {
	const { data: servicesProvider } = useCurrentServicesProvider();
	const createInventoryFieldGroupPrecedenceMutation = useCreateInventoryFieldGroupPrecedence();
	const programInfo = useServiceProgramInfo(INVENTORY_SECTIONS.FIELD_PRECEDENCE);

	// Initial form data from servicesProvider
	const [formData, setFormData] = useState<Record<string, InventoryDataSource>>(() =>
		getFormStateFromServicesProvider(servicesProvider),
	);

	// Sync form data when servicesProvider changes
	useEffect(() => {
		setFormData(getFormStateFromServicesProvider(servicesProvider));
	}, [servicesProvider]);

	// Early return if subsection is not part of the program or is Ekho-only
	if (!servicesProvider || !programInfo.shouldRender) {
		return null;
	}

	// Convert field group configs to table rows
	const tableRows: FieldGroupRow[] = Object.entries(INVENTORY_FIELD_GROUP_CONFIGS).map(
		([fieldGroup, config]) => ({
			id: fieldGroup,
			fieldGroup: fieldGroup as InventoryFieldGroup,
			name: config.name,
			description: config.description,
			selectedSource: formData[fieldGroup as InventoryFieldGroup] || config.defaultSource,
			availableSources: config.availableSources,
		}),
	);

	// Handle form save
	const handleSave = async () => {
		if (!servicesProvider) return;

		try {
			await createInventoryFieldGroupPrecedenceMutation.mutateAsync({
				fieldGroupPrecedence: getConfigFromFormData(formData),
			});
		} catch (error) {
			createLog(
				'config-mgmt',
				`Failed to update field group precedence: ${
					error instanceof Error ? error.message : String(error)
				}`,
				LogSeverity.ERROR,
			);
			// TODO: Handle error state (show user feedback, revert changes, etc.)
			throw error;
		}
	};

	// Handle form changes to update field group selection
	const handleChange = (fieldGroup: InventoryFieldGroup, selectedValue: string) => {
		setFormData((prev) => ({
			...prev,
			[fieldGroup]: selectedValue as InventoryDataSource,
		}));
	};

	// Define table columns
	const columns: TableColumn<FieldGroupRow>[] = [
		{
			id: 'fieldGroup',
			title: 'Field group',
			renderCell: (row) => <Text>{row.name}</Text>,
		},
		{
			id: 'description',
			title: 'Description',
			renderCell: (row) => <Text>{row.description}</Text>,
		},
		{
			id: 'selectedSource',
			title: 'Default source',
			renderCell: (row) => {
				// If only one source available, show as read-only text
				if (row.availableSources.length === 1) {
					return <Text>{getDataSourceLabel(row.selectedSource)}</Text>;
				}

				// Otherwise show dropdown
				return (
					<Dropdown
						options={row.availableSources.map((source) => ({
							id: source,
							label: getDataSourceLabel(source),
						}))}
						value={row.selectedSource}
						onSelectionChange={(selectedValue) =>
							handleChange(row.fieldGroup, selectedValue)
						}
						placeholder='Select source'
					/>
				);
			},
		},
	];

	return (
		<Box padding='24' background='primary' rounding='12' elevation='0'>
			<Stack itemsSpacing='24'>
				<Text size='title300' weight='bold'>
					Set your data source preferences
				</Text>

				<Box padding='20' background='secondary'>
					<Stack itemsSpacing='8'>
						<Group itemsAlignX='start' itemsSpacing='8'>
							<InfoIcon />
							<Text size='body' weight='semibold'>
								Important things to know
							</Text>
						</Group>
						<Stack>
							<Text size='body' color='secondary'>
								• After your sources are connected, all data will sync automatically
								until you manually edit a field. Manual edits override syncing.
							</Text>
							<Text size='body' color='secondary'>
								• Certain fields always pull from a fixed source (like VIN from DMS)
								and can&apos;t be changed.
							</Text>
							<Text size='body' color='secondary'>
								• You can update these mappings anytime in Settings after completing
								onboarding.
							</Text>
						</Stack>
					</Stack>
				</Box>

				<Stack itemsSpacing='24'>
					<Text size='heading'>Vehicle</Text>
					<Table
						columns={columns}
						items={tableRows}
						getItemId={(row) => row.id}
						aria-label='Field group precedence table'
					/>
				</Stack>

				<Group itemsAlignX='end'>
					<Button
						onPress={handleSave}
						isDisabled={createInventoryFieldGroupPrecedenceMutation.isPending}
						isLoading={createInventoryFieldGroupPrecedenceMutation.isPending}
						aria-label='Save field group precedence'>
						Save
					</Button>
				</Group>
			</Stack>
		</Box>
	);
}

export default FieldGroupPrecedenceForm;
